import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from "react";
import { Tooltip } from "../Tooltip/Tooltip.component.js";
import { defaultStyle } from "./TooltipOnTextOverflow.contants.js";
import { TooltipOnTextOverflowProps } from "./TooltipOnTextOverflow.types.js";

const isOverflowing = (ref: React.RefObject<HTMLElement>): boolean => {
    const { current } = ref;

    if (!current) return false;

    return current.scrollWidth > current.clientWidth;
};

export const TooltipOnTextOverflow = forwardRef<HTMLDivElement, TooltipOnTextOverflowProps>(
    ({ children, tag = "span", style, tooltipProps, disabled, delay = 0, ...props }, ref) => {
        const reference = useRef<HTMLDivElement>(null);
        const [overflow, setOverflow] = React.useState(false);

        useImperativeHandle(ref, () => reference.current!);

        const component = React.createElement(
            tag,
            {
                ref: reference,
                style: { ...defaultStyle, ...style },
                ...props,
            },
            children,
        );

        useLayoutEffect(() => {
            const element = reference.current;
            if (!element) return undefined;

            const updateOverflow = () => {
                const newOverflow = isOverflowing(reference);
                setOverflow((prev) => (prev !== newOverflow ? newOverflow : prev));
            };

            updateOverflow();

            const resizeObserver = new ResizeObserver(updateOverflow);
            resizeObserver.observe(element);

            return () => resizeObserver.disconnect();
        }, [children]);

        return overflow && !disabled ? (
            <Tooltip ref={reference} {...tooltipProps} content={children} delay={delay}>
                {component}
            </Tooltip>
        ) : (
            component
        );
    },
);
